import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { consumePendingInitialMessage } from '~/utils/initialMessageTransport'

interface UseChatLifecycleParams<T> {
  conversationUuid: string | undefined
  isStreaming: boolean
  isProcessing: boolean
  isLoading: boolean
  refetch: (silent?: boolean) => Promise<unknown>
  /** Identifies an optimistic message for deduplication (e.g. msg.id) */
  getOptimisticId: (msg: T) => string
}

/**
 * Shared lifecycle hook for chat components.
 *
 * Manages: optimistic messages, streaming/processing lifecycle,
 * hasStreamedInSession flag, initial-message URL param, refetch timing,
 * and processing-end refetch for refreshed clients.
 *
 * Used by both ChatDisplayController (StreamingMessage) and chat home flows.
 */
export function useChatLifecycle<T>({
  conversationUuid,
  isStreaming,
  isProcessing,
  isLoading,
  refetch,
  getOptimisticId
}: UseChatLifecycleParams<T>) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [optimisticMessages, setOptimisticMessages] = useState<T[]>([])
  const [hasStreamedInSession, setHasStreamedInSession] = useState(false)
  const [hasProcessedInitialMessage, setHasProcessedInitialMessage] = useState(false)
  const pendingInitialMessageRef = useRef<string | null>(null)

  // Whether the post-stream refetch is still in flight.
  // While true, streaming messages should remain visible to avoid a flash.
  const [pendingStreamRefetch, setPendingStreamRefetch] = useState(false)

  // ── Reset when conversation changes ──────────────────────────────────
  useEffect(() => {
    setOptimisticMessages([])
    setHasStreamedInSession(false)
    setHasProcessedInitialMessage(false)
    setPendingStreamRefetch(false)
    pendingInitialMessageRef.current = consumePendingInitialMessage(conversationUuid || '')
  }, [conversationUuid])

  // ── Streaming lifecycle ────────────────────────────────────────────────
  //
  // Handles client-side streaming (SSE) end.  The separate processing-end
  // refetch below covers refreshed clients that never had a live stream.
  //
  // useLayoutEffect so that setPendingStreamRefetch(true) triggers a
  // synchronous re-render BEFORE the browser paints. Without this, there is
  // one painted frame where isStreaming=false and pendingStreamRefetch is
  // still false, causing streaming messages to briefly disappear.
  const onStreamingDoneRef = useRef<(() => void) | null>(null)
  const prevIsStreamingRef = useRef(false)
  const refetchTokenRef = useRef(0)

  useLayoutEffect(() => {
    const streamingJustFinished = prevIsStreamingRef.current && !isStreaming

    if (isStreaming && !prevIsStreamingRef.current) {
      setHasStreamedInSession(true)
    }

    prevIsStreamingRef.current = isStreaming

    if (streamingJustFinished) {
      onStreamingDoneRef.current?.()
      setPendingStreamRefetch(true)

      const token = ++refetchTokenRef.current
      refetch(true).finally(() => {
        if (refetchTokenRef.current !== token) return
        setPendingStreamRefetch(false)
      })
    }
  }, [isStreaming, refetch])

  // ── Processing-end refetch (for refreshed clients) ───────────────────
  //
  // When a client refreshes mid-processing, it never had a live SSE stream,
  // so the streaming-end refetch above never fires.  Instead, the client shows
  // server-side snapshot messages.  When processing finishes (is_processing
  // flips false via WS), we need to refetch so persisted messages replace the
  // snapshot — otherwise the snapshot disappears with nothing to take its place.
  const prevIsProcessingRef = useRef(isProcessing)
  useEffect(() => {
    if (prevIsProcessingRef.current && !isProcessing && !hasStreamedInSession) {
      refetch(true)
    }
    prevIsProcessingRef.current = isProcessing
  }, [isProcessing, hasStreamedInSession, refetch])

  // ── Snapshot polling for refreshed clients ──────────────────────────
  //
  // After a refresh, two gaps prevent real-time snapshot delivery:
  //  1. WS needs time to handshake/authenticate before events arrive
  //  2. The first DB snapshot may not exist yet (saved 2s after stream starts)
  //
  // Poll every 3s to bridge this gap. Once WS connects and delivers
  // updates (via CurrentConversationContext merging conversation_metadata),
  // the polls become harmless no-ops since the data is already fresh.
  // Stops automatically when processing ends or the user starts a new stream.
  useEffect(() => {
    if (!isProcessing || isStreaming || hasStreamedInSession) return
    const interval = setInterval(() => {
      refetch(true)
    }, 3000)
    return () => clearInterval(interval)
  }, [isProcessing, isStreaming, hasStreamedInSession, refetch])

  // ── Optimistic message helpers ───────────────────────────────────────
  const addOptimistic = useCallback((msg: T) => {
    setOptimisticMessages(prev => [...prev, msg])
  }, [])

  const removeOptimistic = useCallback(
    (id: string) => {
      setOptimisticMessages(prev => prev.filter(m => getOptimisticId(m) !== id))
    },
    [getOptimisticId]
  )

  // ── Initial message URL param ────────────────────────────────────────
  const clearUrlParams = useCallback(() => {
    const newParams = new URLSearchParams(searchParams)
    newParams.delete('initial_message')
    newParams.delete('initial_message_is_speech_input')
    setSearchParams(newParams)
  }, [searchParams, setSearchParams])

  const initialMessageFromUrl = searchParams.get('initial_message')
  const initialMessage = initialMessageFromUrl ?? pendingInitialMessageRef.current
  const initialMessageIsSpeechInput =
    !!initialMessageFromUrl && searchParams.get('initial_message_is_speech_input') === 'true'

  const consumeInitialMessage = useCallback(() => {
    setHasProcessedInitialMessage(true)
    pendingInitialMessageRef.current = null
    if (searchParams.get('initial_message')) {
      setTimeout(() => clearUrlParams(), 100)
    }
  }, [searchParams, clearUrlParams])

  const canProcessInitialMessage =
    !!initialMessage && !hasProcessedInitialMessage && !isStreaming && !isLoading

  return {
    optimisticMessages,
    addOptimistic,
    removeOptimistic,
    hasStreamedInSession,
    /** True while the post-stream refetch is in flight; consumers should keep showing
     *  streaming messages until this flips to false to avoid flash/duplication. */
    pendingStreamRefetch,
    /** Ref for extra cleanup when streaming finishes (e.g. clear feedbackOverrides) */
    onStreamingDoneRef,
    /** The initial message from URL or pending transport (null if none) */
    initialMessage: canProcessInitialMessage ? initialMessage : null,
    /** Whether the initial message came from browser speech input */
    initialMessageIsSpeechInput: canProcessInitialMessage && initialMessageIsSpeechInput,
    /** Call after sending the initial message to consume it */
    consumeInitialMessage
  }
}
